import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import AppSidebar from "@/components/AppSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, ArrowRight, Loader2, ChevronRight, AlertTriangle, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tokenHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("heyintake_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AdminLeadIntelligencePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [unowned, setUnowned] = useState({ leads: 0, runs: 0, drafts: 0 });
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState(null);
  // Transfer state
  const [targetEmail, setTargetEmail] = useState("");
  const [dryRun, setDryRun] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/lead-intel/workspaces`, { headers: tokenHeader() });
      setWorkspaces(r.data.workspaces || []);
      setUnowned(r.data.unowned || { leads: 0, runs: 0, drafts: 0 });
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Platform admin access required");
        navigate("/dashboard");
        return;
      }
      toast.error("Failed to load workspaces");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const filteredWorkspaces = useMemo(() => {
    const s = (search || "").trim().toLowerCase();
    if (!s) return workspaces;
    return workspaces.filter(w =>
      (w.email || "").toLowerCase().includes(s)
      || (w.business_name || "").toLowerCase().includes(s)
      || (w.id || "").toLowerCase().includes(s)
    );
  }, [workspaces, search]);

  const toggleEntitlement = async (ws, next) => {
    setSavingId(ws.id);
    try {
      await axios.put(
        `${API}/admin/lead-intel/entitlements/${ws.id}`,
        { lead_intelligence: next },
        { headers: tokenHeader() },
      );
      setWorkspaces(prev => prev.map(w => w.id === ws.id ? { ...w, lead_intelligence_enabled: next } : w));
      toast.success(`Lead Intelligence ${next ? "enabled" : "disabled"} for ${ws.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update entitlement");
    } finally {
      setSavingId(null);
    }
  };

  const runPreview = async () => {
    const email = (targetEmail || "").trim();
    if (!email) {
      toast.error("Enter a target email");
      return;
    }
    setPreviewing(true);
    setDryRun(null);
    setLastResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/lead-intel/transfer-ownership`,
        { target_email: email, dry_run: true },
        { headers: tokenHeader() },
      );
      setDryRun(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
      setDryRun(null);
    } finally {
      setPreviewing(false);
    }
  };

  const runExecute = async () => {
    if (!dryRun || !dryRun.target_email) return;
    setExecuting(true);
    try {
      const r = await axios.post(
        `${API}/admin/lead-intel/transfer-ownership`,
        { target_email: dryRun.target_email, dry_run: false },
        { headers: tokenHeader() },
      );
      setLastResult(r.data);
      setDryRun(null);
      setTargetEmail("");
      setConfirmOpen(false);
      toast.success(`Transferred ${r.data.transferred.total} records to ${r.data.target_email}`);
      // Refresh table so counts update
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-testid="admin-lead-intel-page">
      <AppSidebar user={user} onLogout={logout} />
      <main className="flex-1 ml-64 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-rose-600 font-medium flex items-center gap-2">
                <ShieldAlert className="w-3.5 h-3.5" /> Platform Admin
              </div>
              <h1 className="text-3xl font-semibold text-zinc-900 mt-1">Lead Intelligence Admin</h1>
              <p className="text-zinc-500 text-sm mt-1">
                Manage workspace entitlements and transfer ownership of unowned Lead Intelligence data.
              </p>
            </div>
          </div>

          {/* Unowned banner */}
          {(unowned.leads + unowned.runs + unowned.drafts) > 0 && (
            <Card className="rounded-2xl border-0 bg-amber-50/60" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }} data-testid="unowned-banner">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-700" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-amber-900">Unowned Lead Intelligence data</div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    {unowned.leads} leads · {unowned.runs} runs · {unowned.drafts} drafts are not associated with any workspace.
                    Use the Transfer Ownership panel below to assign them to a workspace.
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Workspace table */}
          <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base">Workspaces</CardTitle>
              <Input
                placeholder="Search workspaces"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-64 h-9 text-sm"
                data-testid="workspace-search"
              />
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
              ) : filteredWorkspaces.length === 0 ? (
                <div className="py-10 text-center text-zinc-400 text-sm">No workspaces match the filter.</div>
              ) : (
                <div className="border-t border-zinc-100">
                  <table className="w-full text-sm" data-testid="workspaces-table">
                    <thead>
                      <tr className="text-xs text-zinc-500 uppercase tracking-wide">
                        <th className="text-left font-medium py-2.5 pr-4">Workspace</th>
                        <th className="text-left font-medium py-2.5 pr-4">Plan</th>
                        <th className="text-right font-medium py-2.5 pr-4">LI Records</th>
                        <th className="text-right font-medium py-2.5">Lead Intelligence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWorkspaces.map(ws => (
                        <tr key={ws.id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`workspace-row-${ws.id}`}>
                          <td className="py-3 pr-4">
                            <div className="font-medium text-zinc-900">{ws.business_name || "(unnamed)"}</div>
                            <div className="text-xs text-zinc-500">{ws.email}</div>
                          </td>
                          <td className="py-3 pr-4">
                            <Badge variant="outline" className="text-[10px] uppercase tracking-wide border-zinc-200 bg-zinc-50 text-zinc-600">
                              {ws.subscription_plan || "free"}
                            </Badge>
                            {ws.is_admin && (
                              <Badge variant="outline" className="ml-1.5 text-[10px] uppercase tracking-wide border-rose-200 bg-rose-50 text-rose-700">
                                Admin
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums text-zinc-600">
                            <span title={`${ws.li_leads_count} leads / ${ws.li_runs_count} runs / ${ws.li_drafts_count} drafts`}>
                              {ws.li_leads_count + ws.li_runs_count + ws.li_drafts_count}
                            </span>
                          </td>
                          <td className="py-3 text-right">
                            <div className="inline-flex items-center gap-2">
                              {savingId === ws.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />}
                              <Switch
                                checked={!!ws.lead_intelligence_enabled}
                                onCheckedChange={(next) => toggleEntitlement(ws, next)}
                                disabled={savingId === ws.id}
                                data-testid={`toggle-li-${ws.id}`}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transfer Ownership */}
          <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }} data-testid="transfer-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-zinc-500" />
                Transfer Ownership
              </CardTitle>
              <p className="text-xs text-zinc-500 mt-1">
                Claim all unowned Lead Intelligence data (leads, runs, drafts) for an existing workspace.
                Idempotent — re-running has no effect. A preview step is required before execution.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="md:col-span-2">
                  <Label className="text-xs">Target workspace email</Label>
                  <Input
                    type="email"
                    placeholder="studio@okliko.com"
                    value={targetEmail}
                    onChange={e => setTargetEmail(e.target.value)}
                    data-testid="transfer-target-email"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={runPreview}
                  disabled={previewing || !targetEmail.trim()}
                  data-testid="transfer-preview-btn"
                >
                  {previewing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
                  Preview transfer
                </Button>
              </div>

              {dryRun && (
                <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-4 space-y-3" data-testid="transfer-preview">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Preview</div>
                  <div className="text-sm text-zinc-700">
                    Target: <span className="font-medium text-zinc-900">{dryRun.target_business_name || "(unnamed)"}</span>
                    {" "}<span className="text-zinc-500">({dryRun.target_email})</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center text-sm">
                    <div className="rounded-lg bg-white border border-zinc-200 py-3">
                      <div className="text-2xl font-semibold text-zinc-900 tabular-nums" data-testid="transfer-preview-leads">{dryRun.would_transfer.leads}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Leads</div>
                    </div>
                    <div className="rounded-lg bg-white border border-zinc-200 py-3">
                      <div className="text-2xl font-semibold text-zinc-900 tabular-nums" data-testid="transfer-preview-runs">{dryRun.would_transfer.runs}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Runs</div>
                    </div>
                    <div className="rounded-lg bg-white border border-zinc-200 py-3">
                      <div className="text-2xl font-semibold text-zinc-900 tabular-nums" data-testid="transfer-preview-drafts">{dryRun.would_transfer.drafts}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Drafts</div>
                    </div>
                  </div>
                  <div className="text-xs text-zinc-500">
                    Entitlement after transfer: <span className="font-medium text-zinc-700">lead_intelligence = true</span>
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="ghost" size="sm" onClick={() => setDryRun(null)} data-testid="transfer-cancel-preview">Cancel</Button>
                    <Button
                      size="sm"
                      onClick={() => setConfirmOpen(true)}
                      disabled={dryRun.would_transfer.total === 0}
                      className="bg-zinc-900 hover:bg-zinc-800"
                      data-testid="transfer-execute-btn"
                    >
                      Execute transfer <ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {lastResult && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="transfer-result">
                  <div className="text-xs uppercase tracking-wider text-emerald-700 font-medium">Transfer complete</div>
                  <div className="text-sm text-emerald-900 mt-1">
                    Transferred <span className="font-medium">{lastResult.transferred.total}</span> records to {lastResult.target_email}
                    {" "}({lastResult.transferred.leads} leads · {lastResult.transferred.runs} runs · {lastResult.transferred.drafts} drafts).
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Confirm modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="transfer-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Confirm ownership transfer
            </DialogTitle>
          </DialogHeader>
          {dryRun && (
            <div className="text-sm text-zinc-600 leading-relaxed space-y-3">
              <p>
                You are about to assign <span className="font-medium text-zinc-900">{dryRun.would_transfer.total}</span> Lead
                Intelligence records (<span className="tabular-nums">{dryRun.would_transfer.leads}</span> leads,
                {" "}<span className="tabular-nums">{dryRun.would_transfer.runs}</span> runs,
                {" "}<span className="tabular-nums">{dryRun.would_transfer.drafts}</span> drafts) to:
              </p>
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3">
                <div className="font-medium text-zinc-900">{dryRun.target_business_name || "(unnamed)"}</div>
                <div className="text-xs text-zinc-500">{dryRun.target_email}</div>
              </div>
              <p className="text-xs text-zinc-500">
                This will also set <code className="bg-zinc-100 px-1 py-0.5 rounded text-[11px]">entitlements.lead_intelligence = true</code> on the target.
                The action is idempotent but cannot be selectively undone.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={executing} data-testid="transfer-confirm-cancel">
              Cancel
            </Button>
            <Button onClick={runExecute} disabled={executing} className="bg-zinc-900 hover:bg-zinc-800" data-testid="transfer-confirm-execute">
              {executing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              I understand, execute transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
